import { prepareCanvasDocx } from './prepareCanvasDocx';
import type { CanvasDraftKind, DocxIntegrityIssue } from '@hector/shared-types';
import { CANVAS_DRAFT_TRANSFER_TIMEOUT_MS, canvasDraftApi, canvasDocxTooLargeMessage, canvasDocxVerificationUnavailableMessage } from '../api/canvasDraft.api';

const DEFAULT_DEBOUNCE_MS = 2_500;
/**
 * Borne du PUT de brouillon (#1334/D5).
 *
 * **La même constante que le GET de contenu**, importée et non recopiée : les
 * deux bornent le même transfert de `.docx` sur la même route, et une valeur
 * jumelle liée par un simple commentaire dérive en silence. Elle est
 * load-bearing ailleurs qu'ici — le refroidissement LRU d'un résident ATTEND ce
 * checkpoint (`canvasResidenceCapacity.checkpointAndCool`) et la transaction de
 * résidence est sérialisée : sans borne, un PUT muet gelait l'ouverture de tout
 * document suivant, définitivement.
 */
const DEFAULT_PERSIST_TIMEOUT_MS = CANVAS_DRAFT_TRANSFER_TIMEOUT_MS;
/**
 * Report de l'autosave différé tant qu'une écriture est en cours dans la session.
 *
 * ⚠️ L'export et l'écriture ne cohabitent pas (mesuré le 2026-08-27,
 * `mesure-bascule-lecture-seule.spec.ts`) : pendant `instance.export()`, SuperDoc
 * refuse toute écriture (`review-surface-read-only`, message trompeur « read-only
 * review mode »). Sans report, le débounce expirait au milieu d'une écriture
 * longue — en prod (#1404, conv `896b7bf8`) la création native d'un bordereau de
 * 63 pièces a été refusée à l'item 47, et l'acte enregistré est parti tronqué.
 * Valeurs reprises de `8d3b24ed5` (report perdu par #1046).
 */
const DEFAULT_WRITE_DEFER_MS = 400;
const EMPTY_ZIP_SIZE = 22;
const ZIP_LOCAL_FILE_HEADER = [0x50, 0x4b, 0x03, 0x04] as const;

export type CanvasDraftSaveStatus = 'idle' | 'unsaved' | 'saving' | 'saved' | 'error';

export interface CanvasDraftExportInstance {
  export(options: { exportType: ['docx']; triggerDownload: false }): Promise<Blob>;
}

export interface CanvasDraftSessionContext {
  conversationId: string | null;
  caseId: string | null;
  kind: CanvasDraftKind;
  title: string;
  sourceDocumentId: string | null;
}

export interface CanvasDraftPersistenceRequest extends CanvasDraftSessionContext {
  draftId: string;
  version: number;
  attemptId: string;
  blob: Blob;
}

export interface CanvasDraftControllerSnapshot {
  conversationId: string | null;
  draftId: string | null;
  version: number;
  savedVersion: number;
  status: CanvasDraftSaveStatus;
  /**
   * Set only while `status` is `error`, for a failure that has its own wording:
   * the server could not complete its structural check (#1743) — « Réessayer »
   * replays the same snapshot, so the generic « n’a pas pu être sauvegardé »
   * would alarm for nothing; or the document exceeds a size ceiling of the
   * check (#1744) — only splitting or lightening it helps.
   */
  failureMessage?: string;
}

/** State required to put the draft identity back on A after B fails to commit. */
export interface CanvasDraftRecoveryState {
  readonly context: CanvasDraftSessionContext;
  readonly draftId: string | null;
  /** Exact integrity attempt acknowledged for this durable draft, if any. */
  readonly acknowledgedAttemptId?: string;
  readonly version: number;
  readonly savedVersion: number;
}

/** Durable identity acknowledged by one exact draft checkpoint. */
export interface CanvasDraftCheckpointReceipt {
  conversationId: string | null;
  draftId: string;
  version: number;
  attemptId?: string;
}

/** Only the completed action inside the mutation queue may exempt its own activity. */
export interface CanvasCheckpointOptions {
  afterCompletedAgentMutation?: boolean;
}

export class CanvasDocxNotSavableError extends Error {
  readonly errorCode = 'CANVAS_DOCX_NOT_SAVABLE';
  constructor(readonly issues: readonly DocxIntegrityIssue[], readonly recoveryAvailable = false) {
    super('Le document n’a pas pu être enregistré : une correction de format est nécessaire.');
  }
}

/**
 * Ce que le MONTAGE dit au contrôleur de la source qu'il vient d'exposer.
 *
 * Le contrôleur ne connaît ni les gabarits ni les brouillons restaurés : il
 * connaît des versions et des octets. Ces deux faits vivent donc dans la
 * demande, pas dans une devinette interne (#1334/D2).
 */
export interface CheckpointInitialSource {
  /**
   * La source montée : les octets que la présentation a posés
   * (`templateOverride`). Un montage réannoncé porte les mêmes et ne rejoue rien.
   *
   * ⚠️ **La clé est la SOURCE, jamais le montage** (D2 de #1337). Une source
   * survit à ses remontages ; clée sur l'époque, elle ré-armerait un checkpoint
   * à chaque remontage — et, sous D1, à chaque éditeur que SuperDoc
   * reconstruit —, c'est-à-dire le round-trip DOCX répété de #1334. Comparés
   * par référence, et c'est juste : chaque pose fabrique un objet neuf
   * (téléchargement d'une pièce, export, GET du brouillon), tandis que tout ce
   * qui la transporte sans la changer — clone de présentation, renommage,
   * refroidissement, remontage à l'identique — garde la même référence.
   */
  readonly octets: Blob;
  /**
   * `true` quand les octets montés viennent du SERVEUR — brouillon restauré, ou
   * réhydratation d'un résident refroidi. Aucun checkpoint initial n'y a de
   * sens. ⚠️ Ne pas le déduire du seul `activeDraft` : la réhydratation LRU le
   * laisse à `null` tout en avançant la version (revue #1334).
   */
  readonly estLeBrouillonDurable: boolean;
}

export type CanvasDraftOwnerDecision =
  | { accepted: true }
  | {
    accepted: false;
    reason: 'owner-mismatch';
    expectedConversationId: string | null;
    receivedConversationId: string | null;
  };

export interface CanvasDraftControllerDependencies {
  persist(request: CanvasDraftPersistenceRequest, signal: AbortSignal): Promise<{ integrityAttemptId?: string } | void>;
  createDraftId(): string;
  debounceMs?: number;
  persistTimeoutMs?: number;
  /**
   * Vrai tant qu'une écriture est en cours dans CETTE session documentaire.
   * Injecté par `CanvasDocumentScopeDirectory` : le contrôleur ne connaît ni
   * SuperDoc ni le registre, il demande seulement « puis-je exporter ? ».
   */
  isWriting?: (options?: CanvasCheckpointOptions) => boolean;
  writeDeferMs?: number;
}

interface CapturedSave extends CanvasDraftSessionContext {
  draftId: string;
  version: number;
  instance: CanvasDraftExportInstance | null;
  generation: number;
  attemptId: string;
}

interface RetryableCheckpoint {
  captured: CapturedSave;
  blob: Blob | null;
}

export async function canvasDocxArchiveHasEntries(blob: Blob): Promise<boolean> {
  if (blob.size <= EMPTY_ZIP_SIZE) return false;
  try {
    const head = new Uint8Array(await blob.slice(0, ZIP_LOCAL_FILE_HEADER.length).arrayBuffer());
    return ZIP_LOCAL_FILE_HEADER.every((byte, index) => head[index] === byte);
  } catch {
    // Some browser/test Blob implementations cannot inspect slices. Size still
    // rules out the canonical empty ZIP, so refusing here would lose valid work.
    return true;
  }
}

async function persistCanvasDraft(
  request: CanvasDraftPersistenceRequest,
  signal: AbortSignal,
): Promise<{ integrityAttemptId?: string }> {
  return canvasDraftApi.upsertDraft({
    id: request.draftId,
    attemptId: request.attemptId,
    conversationId: request.conversationId,
    caseId: request.caseId,
    kind: request.kind,
    title: request.title,
    sourceDocumentId: request.sourceDocumentId,
    blob: request.blob,
  }, signal);
}

/**
 * Owns the draft lifecycle of one exact document session.
 *
 * The controller deliberately has no concept of a visible/current canvas. A
 * scope keeps it alive across editor remounts, while every save captures the
 * instance, owner, draft id and version that produced it. Its promise chain is
 * private and must never be shared with the editor mutation queue.
 */
export class CanvasDraftSessionController {
  private context: CanvasDraftSessionContext;
  private instance: CanvasDraftExportInstance | null = null;
  private draftId: string | null = null;
  private version = 0;
  private savedVersion = 0;
  private status: CanvasDraftSaveStatus = 'idle';
  private failureMessage: string | null = null;
  private pending: CapturedSave | null = null;
  private debounceTimer: ReturnType<typeof setTimeout> | null = null;
  private saveChain: Promise<void> = Promise.resolve();
  private readonly exportsInFlight = new Set<Promise<Blob>>();
  private readonly checkpointOperations = new Map<string, Promise<CanvasDraftCheckpointReceipt>>();
  private retryableCheckpoint: RetryableCheckpoint | null = null;
  private generation = 0;
  private disposed = false;
  /** Une source ouverte attend son premier checkpoint (voir `confirmerLeCheckpointInitial`). */
  private checkpointInitialArme = false;
  private checkpointInitialLance = false;
  /**
   * Les octets de la source dont le checkpoint initial a déjà été traité.
   *
   * C'est la clé d'idempotence, et elle ne peut pas être un booléen : un
   * contrôleur vit plus longtemps que ses sources (#1334/D2). Un montage
   * réannoncé porte les MÊMES octets et ne doit rien rejouer ; une source
   * REMPLAÇANTE en apporte de nouveaux et a droit à son propre checkpoint.
   * Comparés par RÉFÉRENCE : voir `CheckpointInitialSource.octets`.
   */
  private sourceCheckpointee: Blob | null = null;
  private snapshotCache: CanvasDraftControllerSnapshot;
  private readonly listeners = new Set<() => void>();
  private readonly persist: CanvasDraftControllerDependencies['persist'];
  private readonly createDraftId: CanvasDraftControllerDependencies['createDraftId'];
  private readonly debounceMs: number;
  private readonly persistTimeoutMs: number;
  /** Un abandon par opération en vol, pour couper les PUT d'une génération périmée. */
  private readonly persistAbortControllers = new Map<string, AbortController>();
  private readonly isWriting: (options?: CanvasCheckpointOptions) => boolean;
  private acknowledgedAttemptId: string | undefined;
  private readonly writeDeferMs: number;

  constructor(
    context: CanvasDraftSessionContext,
    dependencies: Partial<CanvasDraftControllerDependencies> = {},
  ) {
    this.context = { ...context };
    this.persist = dependencies.persist ?? persistCanvasDraft;
    this.createDraftId = dependencies.createDraftId ?? (() => crypto.randomUUID());
    this.debounceMs = dependencies.debounceMs ?? DEFAULT_DEBOUNCE_MS;
    this.persistTimeoutMs = dependencies.persistTimeoutMs ?? DEFAULT_PERSIST_TIMEOUT_MS;
    this.isWriting = dependencies.isWriting ?? (() => false);
    this.writeDeferMs = dependencies.writeDeferMs ?? DEFAULT_WRITE_DEFER_MS;
    this.snapshotCache = this.createSnapshot();
  }

  getSnapshot(): CanvasDraftControllerSnapshot {
    return this.snapshotCache;
  }

  /** Captures the durable identity before a destructive document replacement. */
  captureRecoveryState(): CanvasDraftRecoveryState {
    return {
      context: { ...this.context },
      draftId: this.draftId,
      ...(this.acknowledgedAttemptId ? { acknowledgedAttemptId: this.acknowledgedAttemptId } : {}),
      version: this.version,
      savedVersion: this.savedVersion,
    };
  }

  /**
   * Discards candidate B's pending save and restores A's exact persistence
   * identity. The caller must remount A, then mark it dirty and checkpoint it:
   * the old editor instance was destroyed by the replacement.
   */
  restoreRecoveryState(recovery: CanvasDraftRecoveryState): void {
    // ⚠️ LA GARDE CHANGE DE TERME, PAS DE RÔLE (#1336). Elle interdisait de
    // faire changer de DOCUMENT un contrôleur par une restauration ; depuis que
    // la conversation EST le document, c'est elle qu'on confronte. Aucune époque
    // ne s'y ajoute : un contrôleur survit à ses montages (#1334), la borner à
    // une époque refuserait toute restauration après un remontage — c'est-à-dire
    // exactement le cas qu'elle sert.
    if (recovery.context.conversationId !== this.context.conversationId) {
      throw new Error(
        'Canvas draft recovery cannot change conversation '
        + `(expected=${this.context.conversationId ?? 'null'}, `
        + `received=${recovery.context.conversationId ?? 'null'}). Restore the exact Canvas scope.`,
      );
    }
    this.invalidatePending('replacement-rollback');
    this.context = { ...recovery.context };
    this.draftId = recovery.draftId;
    this.acknowledgedAttemptId = recovery.acknowledgedAttemptId;
    this.version = recovery.version;
    this.savedVersion = recovery.savedVersion;
    this.status = recovery.draftId === null
      ? 'idle'
      : recovery.savedVersion >= recovery.version ? 'saved' : 'unsaved';
    this.emit();
  }

  subscribe(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  configureInstance(instance: CanvasDraftExportInstance | null): void {
    this.instance = instance;
    // RÉPARER LE PENDING ARMÉ AVANT LA LIAISON (#1354). Un `markDirty()` venu
    // d'un appelant AUTRE que le checkpoint initial capture `instance: this.instance`
    // au moment de l'armement : posé entre la preuve de montage SuperDoc et
    // l'effet React qui lie l'instance physique, il gelait sa version sur
    // `instance: null` et remontait une fausse erreur d'enregistrement. On lui
    // rend l'instance exacte au lieu de la perdre. Le report d'armement
    // ci-dessous ne couvre QUE le checkpoint initial ; ces deux gardes sont
    // complémentaires, pas redondantes.
    if (
      instance !== null
      && this.pending
      && this.pending.generation === this.generation
      && this.pending.instance === null
    ) {
      this.pending = { ...this.pending, instance };
    }
    // L'armement peut précéder l'éditeur : le scope devient `ready` sur les
    // signaux SuperDoc, et l'instance n'atteint ce contrôleur qu'au rendu React
    // suivant. Sans cette reprise, un document ouvert sans frappe ne serait
    // jamais persisté (#1346) parce que l'arme serait tombée dans le vide.
    if (instance) this.lancerLeCheckpointInitial();
  }

  /**
   * Confirme le premier checkpoint d'une **source fraîchement ouverte**, sans
   * attendre une frappe (#1332, ferme #1346 ; corrigé pour la 2ᵉ source par
   * #1334/D2).
   *
   * Jusqu'ici la persistance suivait la frappe. Un document ouvert par Hector
   * ou par « Ouvrir dans le Canvas » n'en était pas une : l'avocat le lisait,
   * rechargeait son onglet, et il n'y avait rien à restituer — sans qu'aucun
   * message ne l'ait prévenu.
   *
   * ⚠️ **Ce que ce contrôleur ne peut PAS savoir seul.** Un contrôleur vit plus
   * longtemps que ses sources : un remplacement A → B garde le même scope, donc
   * le même contrôleur. La première version de cette méthode était armée à un
   * coup et refusait dès qu'un `draftId` existait — deux refus qui, réunis,
   * laissaient la DEUXIÈME pièce d'un fil non persistée, c'est-à-dire #1346
   * revenu sur le cas courant. Le montage passe donc les deux faits qu'il est
   * seul à connaître :
   * - `octets` — la source posée elle-même. Un montage réannoncé porte les
   *   mêmes : c'est la clé d'idempotence. ⚠️ Des octets neufs ne sont PAS une
   *   preuve de « nouvelle source » : la réhydratation d'un résident refroidi
   *   en télécharge aussi, sans qu'aucune source ne change — d'où le second
   *   champ, que l'appelant est seul à pouvoir renseigner.
   * - `estLeBrouillonDurable` — les octets montés viennent du SERVEUR (brouillon
   *   restauré, ou réhydratation d'un résident refroidi). Le checkpoint y est
   *   sans objet : on renverrait au serveur le contenu qu'il vient précisément
   *   de nous rendre, et le round-trip DOCX répété ferait dériver la base.
   *
   * Reste un refus interne, différé et non définitif : **pas d'instance** — on
   * attend `configureInstance`, on n'abandonne pas.
   *
   * L'appelant n'a rien à attendre : un échec vit dans le statut (« Non
   * enregistré »), là où l'avocat le voit et peut le rejouer.
   */
  confirmerLeCheckpointInitial(source: CheckpointInitialSource): void {
    if (this.disposed) return;
    if (this.sourceCheckpointee === source.octets) return;
    this.sourceCheckpointee = source.octets;
    if (source.estLeBrouillonDurable) {
      // Enregistrée comme traitée — sans quoi un montage réannoncé du même
      // brouillon repasserait ici à chaque signal `ready`.
      this.checkpointInitialArme = false;
      this.checkpointInitialLance = false;
      return;
    }
    this.checkpointInitialArme = true;
    this.checkpointInitialLance = false;
    this.lancerLeCheckpointInitial();
  }

  private lancerLeCheckpointInitial(): void {
    if (!this.checkpointInitialArme || this.checkpointInitialLance || this.disposed) return;
    if (this.instance === null) return;
    this.checkpointInitialLance = true;
    // ⚠️ `markDirty()` SEUL — surtout pas un `checkpoint()` immédiat. Le moteur
    // docx refuse toute écriture pendant qu'un `instance.export()` est en cours,
    // et le montage prêt est précisément l'instant où Hector enchaîne sa
    // première inspection puis ses écritures : exporter là a rendu 25 parcours
    // agent rouges d'un coup (mesuré le 2026-09-09, `capabilities.get
    // indisponible` / `the exact editor is not ready`). Le debounce ordinaire
    // place l'export après cette salve, et réutilise toutes les protections
    // déjà éprouvées du contrôleur — capture d'identité, chaîne de PUT,
    // reprise de la même version après échec.
    this.markDirty();
  }

  /**
   * ⚠️ **Il n'y a plus de garde « ce contexte change de document »** (#1336).
   * Elle comparait un `documentSessionId` que le contexte ne porte plus : depuis
   * que la conversation identifie le document, un contrôleur est rangé sous sa
   * conversation et ne peut recevoir que le contexte de celle-ci. Ce que la
   * garde protégeait — écrire le brouillon d'un document sous l'identité d'un
   * autre — est porté par les trois branches de propriétaire ci-dessous, qui
   * REFUSENT (`bindOwner`) au lieu de lever ; l'appelant qui prépare la session
   * logique convertit ce refus en exception nommée.
   */
  configureContext(context: CanvasDraftSessionContext): CanvasDraftOwnerDecision {
    const currentOwner = this.context.conversationId;
    if (currentOwner !== context.conversationId && currentOwner !== null) {
      return this.bindOwner(context.conversationId);
    }
    if (currentOwner === null && context.conversationId !== null) {
      this.adoptContext(context);
      return { accepted: true };
    }
    this.context = { ...context };
    this.emit();
    return { accepted: true };
  }

  bindOwner(conversationId: string | null): CanvasDraftOwnerDecision {
    const currentOwner = this.context.conversationId;
    if (currentOwner === conversationId) return { accepted: true };
    if (currentOwner === null && conversationId !== null) {
      this.adoptContext({ ...this.context, conversationId });
      return { accepted: true };
    }
    this.invalidatePending('owner-mismatch');
    this.setStatus(this.draftId === null ? 'idle' : 'unsaved');
    return {
      accepted: false,
      reason: 'owner-mismatch',
      expectedConversationId: currentOwner,
      receivedConversationId: conversationId,
    };
  }

  restoreDraft(
    draftId: string,
    conversationId: string | null,
    integrityAttemptId?: string,
  ): CanvasDraftOwnerDecision {
    const decision = this.bindOwner(conversationId);
    if (!decision.accepted) return decision;
    const replacesCurrentDraft = this.draftId !== draftId;
    if (this.draftId !== null && replacesCurrentDraft) {
      this.invalidatePending('draft-replaced');
    }
    this.draftId = draftId;
    // A mounted server draft is already durable. Preserve the server-issued
    // attempt exactly so later `checkpoint()` calls can prove that same ACK;
    // never fabricate an identity while restoring a legacy draft (#1607).
    if (integrityAttemptId && (replacesCurrentDraft || !this.acknowledgedAttemptId)) {
      this.acknowledgedAttemptId = integrityAttemptId;
    }
    this.emit();
    return decision;
  }

  markDirty(): number {
    if (this.disposed) return this.version;
    this.version += 1;
    // A retry carries the bytes of one exact version. As soon as a later edit
    // exists, replaying the old candidate would only create a stale server
    // attempt; it can never restore the current Canvas state (#1607).
    const retryable = this.retryableCheckpoint;
    if (retryable && retryable.captured.version < this.version) {
      this.retryableCheckpoint = null;
    }
    const draftId = this.draftId ?? this.createDraftId();
    this.draftId = draftId;
    this.pending = {
      ...this.context,
      instance: this.instance,
      draftId,
      version: this.version,
      generation: this.generation,
      attemptId: crypto.randomUUID(),
    };
    const statusChanged = this.status !== 'unsaved';
    this.setStatus('unsaved');
    // The version is part of the public persistence identity. A second edit
    // during the same debounce must publish even though its label remains
    // `unsaved`, otherwise checkpoint/LRU consumers observe the previous edit.
    if (!statusChanged) this.emit();
    this.clearDebounce();
    this.debounceTimer = setTimeout(() => this.onDebounceElapsed(), this.debounceMs);
    return this.version;
  }

  hasExportInFlight(): boolean {
    return this.exportsInFlight.size > 0;
  }

  /** Writers announce activity first, then wait for an export that already began. */
  async waitForExports(): Promise<void> {
    await Promise.allSettled(this.exportsInFlight);
  }

  /** Tracks both persistence and downloads so a new writer never collides with an export. */
  exportDocx(instance: CanvasDraftExportInstance, options?: CanvasCheckpointOptions): Promise<Blob> {
    if (this.isWritingNow(options)) {
      return Promise.reject(this.checkpointError(null, 'document writing or streaming is still active; wait for its completion'));
    }
    const exported = instance.export({ exportType: ['docx'], triggerDownload: false });
    this.exportsInFlight.add(exported);
    void exported.finally(() => this.exportsInFlight.delete(exported)).catch(() => undefined);
    return exported;
  }

  /** Starts the export synchronously while the captured editor is still alive. */
  flush(): void {
    void this.checkpoint().catch(() => undefined);
  }

  /** Never force an export during writing: SuperDoc makes its review surface read-only. */
  private onDebounceElapsed(): void {
    this.debounceTimer = null;
    if (this.isWritingNow()) {
      this.debounceTimer = setTimeout(() => this.onDebounceElapsed(), this.writeDeferMs);
      return;
    }
    this.flush();
  }

  private isWritingNow(options?: CanvasCheckpointOptions): boolean {
    try {
      return this.isWriting(options);
    } catch {
      // Unknown activity is not permission to interrupt a streaming document.
      return true;
    }
  }

  /**
   * Persists the current exact version and resolves only after its PUT ACK.
   *
   * Concurrent callers join the same operation. A failed version retains its
   * captured owner/instance/draft/blob so a later call retries that version
   * without inventing an edit or exporting a different mounted document.
   */
  checkpoint(options?: CanvasCheckpointOptions): Promise<CanvasDraftCheckpointReceipt> {
    if (this.disposed) {
      return Promise.reject(this.checkpointError(null, 'controller is disposed'));
    }
    if (this.isWritingNow(options)) {
      return Promise.reject(this.checkpointError(null, 'document writing or streaming is still active; wait for its completion'));
    }
    const currentOperation = this.checkpointOperations.get(`${this.generation}:${this.version}`);
    if (currentOperation) return currentOperation;
    const retryable = this.retryableCheckpoint;
    if (retryable && !this.isCurrentRetryableVersion(retryable.captured)) {
      this.retryableCheckpoint = null;
    }
    let captured = this.pending
      ?? (retryable && this.isCurrentRetryableVersion(retryable.captured) ? retryable.captured : null);
    if (!captured) {
      if (this.draftId && this.savedVersion >= this.version && this.acknowledgedAttemptId) {
        return Promise.resolve(this.receipt({
          ...this.context,
          instance: this.instance,
          draftId: this.draftId,
          version: this.version,
          generation: this.generation,
          attemptId: this.acknowledgedAttemptId,
        }));
      }
      // Legacy drafts can describe a persisted file but have no server-issued
      // integrity attempt. When an editor is still alive, an explicit durable
      // action must re-export and validate those exact visible bytes instead of
      // treating the historical pointer as proof of the current document.
      if (this.draftId && this.savedVersion >= this.version && this.instance) {
        captured = {
          ...this.context,
          instance: this.instance,
          draftId: this.draftId,
          version: this.version,
          generation: this.generation,
          attemptId: crypto.randomUUID(),
        };
      } else if (this.draftId && this.savedVersion >= this.version) {
        // Keep #1607's diagnostic behaviour for a cold legacy draft: exposing
        // the absence of an acknowledgement is safer than fabricating one.
        return Promise.resolve(this.receipt({
          ...this.context,
          instance: this.instance,
          draftId: this.draftId,
          version: this.version,
          generation: this.generation,
          attemptId: '',
        }));
      }
    }
    if (!captured) {
      return Promise.reject(this.checkpointError(null, 'no persistable captured version is available'));
    }
    if (captured.generation !== this.generation) {
      return Promise.reject(this.checkpointError(captured, 'captured generation is stale'));
    }

    const operationKey = `${captured.generation}:${captured.version}`;
    const joined = this.checkpointOperations.get(operationKey);
    if (joined) return joined;

    if (this.pending === captured) {
      this.pending = null;
      this.clearDebounce();
    }
    const retainedBlob = retryable?.captured === captured ? retryable.blob : null;
    let exported: Promise<Blob>;
    if (retainedBlob) {
      exported = Promise.resolve(retainedBlob);
    } else if (!captured.instance) {
      this.setStatus('unsaved');
      return Promise.reject(this.checkpointError(captured, 'captured editor instance is not ready'));
    } else {
      try {
        exported = this.exportDocx(captured.instance, options);
      } catch (error) {
        this.retenirPourRejeu(captured, null);
        this.failLatest(captured, error, 'export threw before producing the document');
        return Promise.reject(this.checkpointError(captured, 'export threw before producing the document', error));
      }
    }

    const exportOutcome = exported.then(
      (blob) => ({ ok: true as const, blob }),
      (error: unknown) => ({ ok: false as const, error }),
    );
    const operation = this.saveChain
      .catch(() => undefined)
      .then(async (): Promise<CanvasDraftCheckpointReceipt> => {
        if (!this.isGenerationCurrent(captured)) {
          throw this.checkpointError(captured, 'owner or account generation changed before persistence');
        }
        const outcome = await exportOutcome;
        if (!outcome.ok) {
          this.retenirPourRejeu(captured, null);
          this.failLatest(captured, outcome.error, 'export failed before producing the document');
          throw this.checkpointError(captured, 'export failed before producing the document', outcome.error);
        }
        if (!this.isGenerationCurrent(captured)) {
          throw this.checkpointError(captured, 'owner or account generation changed after export');
        }
        const prepared = await prepareCanvasDocx(new Uint8Array(await outcome.blob.arrayBuffer()), captured.instance);
        if (!this.isGenerationCurrent(captured)) {
          throw this.checkpointError(captured, 'owner or account generation changed after archive validation and before persistence');
        }
        // A rejected candidate still goes to the server recovery store. Refusing
        // locally would keep the user's latest work only in this browser tab.
        const candidate = prepared.status === 'rejected'
          ? outcome.blob
          : new Blob([new Uint8Array(prepared.bytes)], { type: outcome.blob.type || 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' });
        if (captured.version === this.version) this.setStatus('saving');
        // ⚠️ AUCUNE BORNE POSÉE ICI, et c'est délibéré. #1354 armait un
        // `AbortController` + `setTimeout` à cet endroit ; `envoyerLeBrouillon`
        // (#1334/D5) en arme un ÉQUIVALENT et plus fort — il double le signal
        // d'une course `Promise.race`, seule protection contre un transport
        // sourd. Les deux enregistrent leur abandon sous la MÊME clé
        // d'opération : le second écrase le premier dans la table, donc le
        // premier n'aurait plus jamais été annulé par `invalidatePending` et sa
        // minuterie aurait couru pour rien. Un seul mécanisme, celui qui borne
        // vraiment.
        try {
          const ack = await this.envoyerLeBrouillon(operationKey, captured, candidate);
          if (prepared.status === 'rejected') throw new CanvasDocxNotSavableError(prepared.issues);
          // An HTTP success alone does not prove which candidate was accepted.
          // Missing/mismatched receipts must never unlock a dossier promotion.
          if (ack?.integrityAttemptId !== captured.attemptId) {
            throw Object.assign(new Error(
              `Canvas draft ACK does not confirm attemptId=${captured.attemptId} for draftId=${captured.draftId}. `
              + 'Keep the current document open and retry saving it; do not repeat the edit.',
            ), { errorCode: 'CANVAS_DRAFT_ACK_MISMATCH' });
          }
          if (this.isGenerationCurrent(captured)) this.acknowledgedAttemptId = ack.integrityAttemptId;
        } catch (error) {
          // Retry the exact bytes sent with this attemptId. Re-exporting or
          // repairing the original again could change the archive hash after a lost ACK.
          this.retenirPourRejeu(captured, candidate);
          this.failLatest(captured, error, 'draft persistence failed');
          throw this.checkpointError(captured, 'draft persistence failed', error);
        }
        if (!this.isGenerationCurrent(captured)) {
          throw this.checkpointError(captured, 'owner or account generation changed before the PUT ACK');
        }
        this.savedVersion = Math.max(this.savedVersion, captured.version);
        if ((this.retryableCheckpoint?.captured.version ?? Number.POSITIVE_INFINITY) <= captured.version) {
          this.retryableCheckpoint = null;
        }
        if (captured.version === this.version) this.setStatus('saved');
        else this.emit();
        return this.receipt(captured);
      });
    this.saveChain = operation.then(() => undefined, () => undefined);
    this.checkpointOperations.set(operationKey, operation);
    void operation.finally(() => {
      if (this.checkpointOperations.get(operationKey) === operation) {
        this.checkpointOperations.delete(operationKey);
      }
    }).catch(() => undefined);
    return operation;
  }

  discard(): void {
    this.invalidatePending('discard');
    this.setStatus('idle');
  }

  clearDraft(): void {
    this.invalidatePending('clear');
    this.draftId = null;
    this.version = 0;
    this.savedVersion = 0;
    // « Nouveau » rend la session à l'état d'un document jamais enregistré. Le
    // réarmement ne dépend plus de ce geste depuis #1334/D2 — chaque source
    // posée apporte ses propres octets —, mais oublier la source traitée reste
    // juste : un document jamais enregistré n'a encore rien checkpointé.
    this.checkpointInitialArme = false;
    this.checkpointInitialLance = false;
    this.sourceCheckpointee = null;
    // Appel direct au lieu de setStatus('idle') : setStatus est gardé par
    // « if (this.status === status) return », qui court-circuite emit() quand
    // le statut est déjà 'idle'. Or draftId peut avoir été posé via
    // restoreDraft() (qui n'avance pas le statut) ou laissé non nul par
    // discard() — dans les deux cas emit() ne serait jamais appelé et
    // snapshotCache.draftId resterait périmé, visible par toutes les gardes
    // qui lisent draftId, dont la logique de restauration stricte de #1586.
    // (#1595)
    this.status = 'idle';
    this.emit();
  }

  dispose(): void {
    if (this.disposed) return;
    this.invalidatePending('dispose');
    this.disposed = true;
    this.instance = null;
    this.status = 'idle';
    this.emit();
    this.listeners.clear();
  }

  /**
   * Envoie le brouillon sous une borne que le CONTRÔLEUR tient (#1334/D5).
   *
   * Le signal est passé au transport — `fetch` l'honore — mais la borne ne lui
   * est pas déléguée : un transport sourd (un faux de test, un polyfill, une
   * couche qui perd l'option) laisserait sinon la promesse en suspens pour
   * toujours, et avec elle la résidence entière. La course est donc la garantie,
   * le signal l'optimisation qui libère réellement la socket.
   */
  private async envoyerLeBrouillon(
    operationKey: string,
    captured: CapturedSave,
    blob: Blob,
  ): Promise<{ integrityAttemptId?: string } | void> {
    const abandon = new AbortController();
    this.persistAbortControllers.set(operationKey, abandon);
    const echeance = setTimeout(() => {
      abandon.abort(new Error(
        `Canvas draft PUT timed out after ${this.persistTimeoutMs}ms `
        + `(conversationId=${captured.conversationId ?? 'null'}, draftId=${captured.draftId}, `
        + `version=${captured.version}). The bytes may or may not have reached the server; `
        + 'retry checkpoint() for this same version — the captured export is retained.',
      ));
    }, this.persistTimeoutMs);
    const abandonne = new Promise<never>((_resolve, reject) => {
      abandon.signal.addEventListener(
        'abort',
        () => reject(abandon.signal.reason instanceof Error
          ? abandon.signal.reason
          : new Error(String(abandon.signal.reason))),
        { once: true },
      );
    });
    try {
      return await Promise.race([
        this.persist({
          conversationId: captured.conversationId,
          caseId: captured.caseId,
          kind: captured.kind,
          title: captured.title,
          sourceDocumentId: captured.sourceDocumentId,
          draftId: captured.draftId,
          version: captured.version,
          attemptId: captured.attemptId,
          blob,
        }, abandon.signal),
        abandonne,
      ]);
    } finally {
      clearTimeout(echeance);
      abandonne.catch(() => undefined);
      if (this.persistAbortControllers.get(operationKey) === abandon) {
        this.persistAbortControllers.delete(operationKey);
      }
    }
  }

  /**
   * Retient un export pour un rejeu de sa version vivante exacte — jamais une
   * génération ou une version déjà supplantée. Une version A qui échoue après
   * `markDirty()` de B peut finir son transport tardivement, mais elle ne doit
   * pas devenir le prochain candidat à exporter : B est désormais le seul
   * brouillon que l'avocat voit et que l'autosave peut rendre durable (#1607).
   */
  private retenirPourRejeu(captured: CapturedSave, blob: Blob | null): void {
    if (!this.isCurrentRetryableVersion(captured)) return;
    this.retryableCheckpoint = { captured, blob };
  }

  /** A persisted retry may only describe the current logical Canvas version. */
  private isCurrentRetryableVersion(captured: CapturedSave): boolean {
    return this.isGenerationCurrent(captured) && captured.version === this.version;
  }

  private isGenerationCurrent(captured: CapturedSave): boolean {
    return !this.disposed
      && captured.generation === this.generation
      && captured.conversationId === this.context.conversationId;
  }

  private adoptContext(context: CanvasDraftSessionContext): void {
    const shouldRequeue = this.draftId !== null && this.instance !== null;
    // Adoption changes the persistence owner of the exact same document. Any
    // pending or in-flight anonymous request may finish on the wire, but its
    // ACK must be stale and the same draftId must be emitted under the adopted
    // conversation before the controller can report `saved` again.
    this.invalidatePending('owner-adoption');
    this.context = { ...context };
    this.emit();
    if (this.draftId !== null) this.setStatus('unsaved');
    if (shouldRequeue) {
      this.markDirty();
      this.flush();
    }
  }

  private invalidatePending(
    reason: 'owner-adoption' | 'owner-mismatch' | 'draft-replaced' | 'discard' | 'clear' | 'dispose' | 'replacement-rollback',
  ): void {
    this.generation += 1;
    // Le PUT d'une génération périmée n'a plus de destinataire : le laisser
    // courir tiendrait la transaction de résidence qui l'attend et pourrait
    // écrire sous une identité que ce contrôleur ne porte plus.
    for (const abandon of this.persistAbortControllers.values()) {
      abandon.abort(new Error(
        `Canvas draft PUT abandoned by ${reason} `
        + `(conversationId=${this.context.conversationId ?? 'null'}).`,
      ));
    }
    this.persistAbortControllers.clear();
    this.pending = null;
    this.retryableCheckpoint = null;
    this.acknowledgedAttemptId = undefined;
    this.clearDebounce();
  }

  private clearDebounce(): void {
    if (this.debounceTimer === null) return;
    clearTimeout(this.debounceTimer);
    this.debounceTimer = null;
  }

  private failLatest(captured: CapturedSave, error: unknown, cause: string): void {
    if (!this.isGenerationCurrent(captured) || captured.version !== this.version) return;
    this.failureMessage = canvasDocxVerificationUnavailableMessage(error) ?? canvasDocxTooLargeMessage(error);
    // Emit even when already in error: the reason may have changed.
    if (this.status === 'error') this.emit();
    else this.setStatus('error');
    console.error(
      '[canvas draft] save refused '
      + `(conversationId=${captured.conversationId ?? 'null'}, `
      + `draftId=${captured.draftId}, version=${captured.version}): ${cause}. `
      + 'The draft remains unsaved; edit or flush the same document session to retry.',
      error,
    );
  }

  private receipt(captured: CapturedSave): CanvasDraftCheckpointReceipt {
    return {
      conversationId: captured.conversationId,
      draftId: captured.draftId,
      version: captured.version,
      ...(this.acknowledgedAttemptId ? { attemptId: this.acknowledgedAttemptId } : {}),
    };
  }

  private checkpointError(captured: CapturedSave | null, cause: string, error?: unknown): Error {
    const draftId = captured?.draftId ?? this.draftId ?? 'none';
    const version = captured?.version ?? this.version;
    const detail = error instanceof Error ? ` Provider detail: ${error.message}.` : '';
    const failure = new Error(
      'Canvas draft checkpoint failed for '
      + `conversationId=${captured?.conversationId ?? this.context.conversationId ?? 'null'} `
      + `draftId=${draftId} version=${version}: ${cause}.${detail} `
      + 'Retry checkpoint() for this same document session; no new edit is required.',
    );
    if (error instanceof CanvasDocxNotSavableError) {
      return Object.assign(failure, { errorCode: error.errorCode, details: { issues: error.issues, recoveryAvailable: error.recoveryAvailable } });
    }
    if (typeof error === 'object' && error !== null) {
      return Object.assign(failure, {
        ...('errorCode' in error ? { errorCode: error.errorCode } : {}),
        ...('details' in error ? { details: error.details } : {}),
      });
    }
    return failure;
  }

  private setStatus(status: CanvasDraftSaveStatus): void {
    if (this.status === status) return;
    this.status = status;
    if (status !== 'error') this.failureMessage = null;
    this.emit();
  }

  private emit(): void {
    this.snapshotCache = this.createSnapshot();
    for (const listener of this.listeners) listener();
  }

  private createSnapshot(): CanvasDraftControllerSnapshot {
    return {
      conversationId: this.context.conversationId,
      draftId: this.draftId,
      version: this.version,
      savedVersion: this.savedVersion,
      status: this.status,
      ...(this.failureMessage ? { failureMessage: this.failureMessage } : {}),
    };
  }
}
